/**
 * Bounding-volume hierarchy over a static triangle soup.
 *
 * Built once at startup and never refit — the arena does not move. The article
 * this project follows (docs/PHYSICS_REFERENCE.md §2) measures ~2000x over the
 * naive all-triangle scan; that ratio is what `test/physics/arena.test.ts`
 * measures against.
 *
 * Layout is flat typed arrays rather than objects:
 *   bounds[6n .. 6n+5]  node AABB, min then max
 *   leftFirst[n]        index of the left child (interior) or of the first
 *                       primitive in `order` (leaf)
 *   primCount[n]        0 for an interior node, else the leaf's primitive count
 * Children are allocated adjacently, so the right child is always
 * `leftFirst[n] + 1`. That is one fewer array and one fewer cache line than
 * storing both child indices.
 *
 * The tree is a pure index structure: it never looks at triangle geometry
 * beyond the AABBs and centroids computed at build time. Both query methods
 * hand triangle indices to a callback, which keeps the intersection maths in
 * triangle.ts and lets the same tree serve sphere, ray and (future) swept
 * queries without duplicating traversal.
 */

const BIN_COUNT = 12;
const LEAF_SIZE = 4;
/** Depth cap for the traversal stack. 64 is far beyond any median-split tree. */
const STACK_SIZE = 64;

/** Result of a nearest-hit ray query. */
export interface BVHRayHit {
  /** Index of the triangle that was hit. */
  triangle: number;
  /** Distance along the ray direction. */
  t: number;
}

/**
 * Per-triangle ray test used by `raycastNearest`. Returns the hit distance, or
 * a negative number for a miss. `maxT` shrinks as closer hits are found, so an
 * implementation may reject anything beyond it.
 */
export type RayIntersector = (triangle: number, maxT: number) => number;

export class BVH {
  /** 9 floats per triangle: ax, ay, az, bx, by, bz, cx, cy, cz. */
  readonly triangles: Float64Array;
  readonly triangleCount: number;
  readonly nodeCount: number;
  /** Deepest leaf, for diagnostics and to prove the stack cap is enough. */
  readonly maxDepth: number;

  private readonly order: Int32Array;
  private readonly bounds: Float64Array;
  private readonly leftFirst: Int32Array;
  private readonly primCount: Int32Array;

  /** Shared traversal stack — queries are not reentrant. */
  private readonly stack = new Int32Array(STACK_SIZE);

  constructor(triangles: Float64Array) {
    if (triangles.length % 9 !== 0) {
      throw new Error(`triangle soup length ${triangles.length} is not a multiple of 9`);
    }
    this.triangles = triangles;
    const n = triangles.length / 9;
    this.triangleCount = n;

    this.order = new Int32Array(n);
    // A binary tree with >= 1 primitive per leaf never exceeds 2n - 1 nodes.
    const maxNodes = Math.max(1, 2 * n);
    this.bounds = new Float64Array(maxNodes * 6);
    this.leftFirst = new Int32Array(maxNodes);
    this.primCount = new Int32Array(maxNodes);

    const triBounds = new Float64Array(n * 6);
    const centroids = new Float64Array(n * 3);
    for (let i = 0; i < n; i++) {
      this.order[i] = i;
      const o = i * 9;
      const ax = triangles[o], ay = triangles[o + 1], az = triangles[o + 2];
      const bx = triangles[o + 3], by = triangles[o + 4], bz = triangles[o + 5];
      const cx = triangles[o + 6], cy = triangles[o + 7], cz = triangles[o + 8];
      const b = i * 6;
      triBounds[b] = Math.min(ax, bx, cx);
      triBounds[b + 1] = Math.min(ay, by, cy);
      triBounds[b + 2] = Math.min(az, bz, cz);
      triBounds[b + 3] = Math.max(ax, bx, cx);
      triBounds[b + 4] = Math.max(ay, by, cy);
      triBounds[b + 5] = Math.max(az, bz, cz);
      const c = i * 3;
      centroids[c] = (ax + bx + cx) / 3;
      centroids[c + 1] = (ay + by + cy) / 3;
      centroids[c + 2] = (az + bz + cz) / 3;
    }

    const built = buildTree(
      this.order, triBounds, centroids,
      this.bounds, this.leftFirst, this.primCount
    );
    this.nodeCount = built.nodeCount;
    this.maxDepth = built.maxDepth;
    if (this.maxDepth >= STACK_SIZE) {
      throw new Error(`BVH depth ${this.maxDepth} exceeds the traversal stack`);
    }
  }

  /** World-space AABB of the whole tree, as [minX, minY, minZ, maxX, maxY, maxZ]. */
  rootBounds(): Float64Array {
    return this.bounds.slice(0, 6);
  }

  /**
   * Visit every triangle whose AABB overlaps the sphere. Conservative: the
   * callback still has to run the exact test. Never misses an overlap, which
   * is what makes the BVH agree with a brute-force scan exactly.
   */
  overlapSphere(
    cx: number, cy: number, cz: number, radius: number,
    visit: (triangle: number) => void
  ): void {
    if (this.triangleCount === 0) return;
    const { bounds, leftFirst, primCount, order, stack } = this;
    const r2 = radius * radius;
    let sp = 0;
    stack[sp++] = 0;
    while (sp > 0) {
      const node = stack[--sp];
      const b = node * 6;
      // Squared distance from the sphere centre to the AABB.
      let d = 0;
      let e = cx < bounds[b] ? bounds[b] - cx : cx > bounds[b + 3] ? cx - bounds[b + 3] : 0;
      d += e * e;
      e = cy < bounds[b + 1] ? bounds[b + 1] - cy : cy > bounds[b + 4] ? cy - bounds[b + 4] : 0;
      d += e * e;
      e = cz < bounds[b + 2] ? bounds[b + 2] - cz : cz > bounds[b + 5] ? cz - bounds[b + 5] : 0;
      d += e * e;
      if (d > r2) continue;

      const count = primCount[node];
      if (count > 0) {
        const first = leftFirst[node];
        for (let i = 0; i < count; i++) visit(order[first + i]);
      } else {
        stack[sp++] = leftFirst[node];
        stack[sp++] = leftFirst[node] + 1;
      }
    }
  }

  /**
   * Visit every triangle whose AABB overlaps the given AABB. Conservative in
   * the same way as `overlapSphere`: the callback still runs the exact test.
   *
   * This is what the car body uses. A bounding SPHERE around the car hitbox has
   * a radius of 77 uu and so reaches 40 uu through the floor from ordinary ride
   * height, which makes every driving tick descend into the floor triangles for
   * nothing; the box's AABB is 19 uu tall and misses them at the root. The
   * difference is the whole cost of the feature.
   */
  overlapAABB(
    minX: number, minY: number, minZ: number,
    maxX: number, maxY: number, maxZ: number,
    visit: (triangle: number) => void
  ): void {
    if (this.triangleCount === 0) return;
    const { bounds, leftFirst, primCount, order, stack } = this;
    let sp = 0;
    stack[sp++] = 0;
    while (sp > 0) {
      const node = stack[--sp];
      const b = node * 6;
      if (
        maxX < bounds[b] || minX > bounds[b + 3] ||
        maxY < bounds[b + 1] || minY > bounds[b + 4] ||
        maxZ < bounds[b + 2] || minZ > bounds[b + 5]
      ) {
        continue;
      }
      const count = primCount[node];
      if (count > 0) {
        const first = leftFirst[node];
        for (let i = 0; i < count; i++) visit(order[first + i]);
      } else {
        stack[sp++] = leftFirst[node];
        stack[sp++] = leftFirst[node] + 1;
      }
    }
  }

  /** Visit every triangle whose AABB the ray segment [0, maxT] passes through. */
  overlapRay(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    maxT: number,
    visit: (triangle: number) => void
  ): void {
    if (this.triangleCount === 0) return;
    const { bounds, leftFirst, primCount, order, stack } = this;
    // Reciprocals are computed once per query; `slabHit` skips the ones whose
    // direction component is zero rather than multiplying by Infinity.
    const ix = 1 / dx;
    const iy = 1 / dy;
    const iz = 1 / dz;
    let sp = 0;
    stack[sp++] = 0;
    while (sp > 0) {
      const node = stack[--sp];
      if (!slabHit(bounds, node * 6, ox, oy, oz, dx, dy, dz, ix, iy, iz, maxT)) continue;
      const count = primCount[node];
      if (count > 0) {
        const first = leftFirst[node];
        for (let i = 0; i < count; i++) visit(order[first + i]);
      } else {
        stack[sp++] = leftFirst[node];
        stack[sp++] = leftFirst[node] + 1;
      }
    }
  }

  /**
   * Nearest ray hit, or null. The direction must be normalized for `t` to be a
   * distance. The traversal shrinks its distance bound as hits are found, so a
   * node lying entirely behind an already-found hit is skipped.
   *
   * THE RESULT IS A SHARED BUFFER, overwritten by the next query on this tree.
   * Read what you need before querying again. Queries were already
   * non-reentrant — the traversal stack is shared too — so this adds no new
   * constraint, and it takes an allocation off a path the suspension runs four
   * times per car per tick.
   */
  /** Result buffer for `raycastNearest`, so a hit costs no allocation. Valid
   *  only until the next query. */
  private readonly hitResult: BVHRayHit = { triangle: -1, t: 0 };

  raycastNearest(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    maxT: number,
    intersect: RayIntersector
  ): BVHRayHit | null {
    if (this.triangleCount === 0) return null;
    const { bounds, leftFirst, primCount, order, stack } = this;
    const ix = 1 / dx;
    const iy = 1 / dy;
    const iz = 1 / dz;
    let bestT = maxT;
    let bestTri = -1;
    let sp = 0;
    stack[sp++] = 0;
    while (sp > 0) {
      const node = stack[--sp];
      if (!slabHit(bounds, node * 6, ox, oy, oz, dx, dy, dz, ix, iy, iz, bestT)) continue;
      const count = primCount[node];
      if (count > 0) {
        const first = leftFirst[node];
        for (let i = 0; i < count; i++) {
          const tri = order[first + i];
          const t = intersect(tri, bestT);
          if (t >= 0 && t < bestT) {
            bestT = t;
            bestTri = tri;
          }
        }
      } else {
        stack[sp++] = leftFirst[node];
        stack[sp++] = leftFirst[node] + 1;
      }
    }
    if (bestTri < 0) return null;
    this.hitResult.triangle = bestTri;
    this.hitResult.t = bestT;
    return this.hitResult;
  }
}

/**
 * Slab test of the ray segment [0, maxT] against node AABB `bounds[b..b+5]`.
 *
 * An axis with a zero direction component is handled by an explicit
 * inside/outside test rather than by letting `0 * Infinity` produce NaN. That
 * case is not exotic: an axis-aligned suspension raycast straight down sits on
 * it for two of three axes on every single query.
 */
const slabHit = (
  bounds: Float64Array, b: number,
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  ix: number, iy: number, iz: number,
  maxT: number
): boolean => {
  let tmin = 0;
  let tmax = maxT;
  let t1: number;
  let t2: number;

  if (dx === 0) {
    if (ox < bounds[b] || ox > bounds[b + 3]) return false;
  } else {
    t1 = (bounds[b] - ox) * ix;
    t2 = (bounds[b + 3] - ox) * ix;
    if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
  }
  if (dy === 0) {
    if (oy < bounds[b + 1] || oy > bounds[b + 4]) return false;
  } else {
    t1 = (bounds[b + 1] - oy) * iy;
    t2 = (bounds[b + 4] - oy) * iy;
    if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
  }
  if (dz === 0) {
    if (oz < bounds[b + 2] || oz > bounds[b + 5]) return false;
  } else {
    t1 = (bounds[b + 2] - oz) * iz;
    t2 = (bounds[b + 5] - oz) * iz;
    if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
  }
  return tmax >= tmin;
};

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

interface BuildResult {
  nodeCount: number;
  maxDepth: number;
}

/**
 * Binned-SAH builder. Splits on the best of 12 bins across all three axes,
 * falling back to a median split whenever SAH cannot separate the primitives
 * (identical centroids, or a partition that leaves one side empty). The median
 * fallback is what guarantees termination.
 */
const buildTree = (
  order: Int32Array,
  triBounds: Float64Array,
  centroids: Float64Array,
  bounds: Float64Array,
  leftFirst: Int32Array,
  primCount: Int32Array
): BuildResult => {
  const n = order.length;
  if (n === 0) {
    bounds.fill(0, 0, 6);
    leftFirst[0] = 0;
    primCount[0] = 0;
    return { nodeCount: 1, maxDepth: 0 };
  }

  let nodesUsed = 1;
  let maxDepth = 0;

  const binCount = new Int32Array(BIN_COUNT);
  const binBounds = new Float64Array(BIN_COUNT * 6);
  const leftArea = new Float64Array(BIN_COUNT);
  const leftCountAcc = new Int32Array(BIN_COUNT);
  const sortScratch: number[] = [];

  const nodeBounds = (node: number, first: number, count: number): void => {
    const b = node * 6;
    let x0 = Infinity, y0 = Infinity, z0 = Infinity;
    let x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    for (let i = first; i < first + count; i++) {
      const t = order[i] * 6;
      if (triBounds[t] < x0) x0 = triBounds[t];
      if (triBounds[t + 1] < y0) y0 = triBounds[t + 1];
      if (triBounds[t + 2] < z0) z0 = triBounds[t + 2];
      if (triBounds[t + 3] > x1) x1 = triBounds[t + 3];
      if (triBounds[t + 4] > y1) y1 = triBounds[t + 4];
      if (triBounds[t + 5] > z1) z1 = triBounds[t + 5];
    }
    bounds[b] = x0; bounds[b + 1] = y0; bounds[b + 2] = z0;
    bounds[b + 3] = x1; bounds[b + 4] = y1; bounds[b + 5] = z1;
  };

  const halfArea = (x: number, y: number, z: number): number =>
    x < 0 || y < 0 || z < 0 ? 0 : x * y + y * z + z * x;

  const medianSplit = (first: number, count: number, axis: number): number => {
    sortScratch.length = 0;
    for (let i = first; i < first + count; i++) sortScratch.push(order[i]);
    sortScratch.sort((a, b) => centroids[a * 3 + axis] - centroids[b * 3 + axis]);
    for (let i = 0; i < count; i++) order[first + i] = sortScratch[i];
    return count >> 1;
  };

  const subdivide = (node: number, first: number, count: number, depth: number): void => {
    if (depth > maxDepth) maxDepth = depth;
    nodeBounds(node, first, count);
    if (count <= LEAF_SIZE) {
      leftFirst[node] = first;
      primCount[node] = count;
      return;
    }

    let bestAxis = -1;
    let bestPos = 0;
    let bestCost = Infinity;

    for (let axis = 0; axis < 3; axis++) {
      let cmin = Infinity;
      let cmax = -Infinity;
      for (let i = first; i < first + count; i++) {
        const c = centroids[order[i] * 3 + axis];
        if (c < cmin) cmin = c;
        if (c > cmax) cmax = c;
      }
      if (cmax - cmin < 1e-12) continue;

      binCount.fill(0);
      for (let k = 0; k < BIN_COUNT; k++) {
        const bb = k * 6;
        binBounds[bb] = Infinity; binBounds[bb + 1] = Infinity; binBounds[bb + 2] = Infinity;
        binBounds[bb + 3] = -Infinity; binBounds[bb + 4] = -Infinity; binBounds[bb + 5] = -Infinity;
      }
      const scale = BIN_COUNT / (cmax - cmin);
      for (let i = first; i < first + count; i++) {
        const tri = order[i];
        let k = ((centroids[tri * 3 + axis] - cmin) * scale) | 0;
        if (k >= BIN_COUNT) k = BIN_COUNT - 1;
        binCount[k]++;
        const t = tri * 6;
        const bb = k * 6;
        if (triBounds[t] < binBounds[bb]) binBounds[bb] = triBounds[t];
        if (triBounds[t + 1] < binBounds[bb + 1]) binBounds[bb + 1] = triBounds[t + 1];
        if (triBounds[t + 2] < binBounds[bb + 2]) binBounds[bb + 2] = triBounds[t + 2];
        if (triBounds[t + 3] > binBounds[bb + 3]) binBounds[bb + 3] = triBounds[t + 3];
        if (triBounds[t + 4] > binBounds[bb + 4]) binBounds[bb + 4] = triBounds[t + 4];
        if (triBounds[t + 5] > binBounds[bb + 5]) binBounds[bb + 5] = triBounds[t + 5];
      }

      // Prefix sweep: cost of putting bins [0, k] left and (k, BIN_COUNT) right.
      let ax0 = Infinity, ay0 = Infinity, az0 = Infinity;
      let ax1 = -Infinity, ay1 = -Infinity, az1 = -Infinity;
      let acc = 0;
      for (let k = 0; k < BIN_COUNT; k++) {
        const bb = k * 6;
        if (binCount[k] > 0) {
          if (binBounds[bb] < ax0) ax0 = binBounds[bb];
          if (binBounds[bb + 1] < ay0) ay0 = binBounds[bb + 1];
          if (binBounds[bb + 2] < az0) az0 = binBounds[bb + 2];
          if (binBounds[bb + 3] > ax1) ax1 = binBounds[bb + 3];
          if (binBounds[bb + 4] > ay1) ay1 = binBounds[bb + 4];
          if (binBounds[bb + 5] > az1) az1 = binBounds[bb + 5];
        }
        acc += binCount[k];
        leftCountAcc[k] = acc;
        leftArea[k] = halfArea(ax1 - ax0, ay1 - ay0, az1 - az0);
      }

      ax0 = Infinity; ay0 = Infinity; az0 = Infinity;
      ax1 = -Infinity; ay1 = -Infinity; az1 = -Infinity;
      let rightAcc = 0;
      for (let k = BIN_COUNT - 1; k > 0; k--) {
        const bb = k * 6;
        if (binCount[k] > 0) {
          if (binBounds[bb] < ax0) ax0 = binBounds[bb];
          if (binBounds[bb + 1] < ay0) ay0 = binBounds[bb + 1];
          if (binBounds[bb + 2] < az0) az0 = binBounds[bb + 2];
          if (binBounds[bb + 3] > ax1) ax1 = binBounds[bb + 3];
          if (binBounds[bb + 4] > ay1) ay1 = binBounds[bb + 4];
          if (binBounds[bb + 5] > az1) az1 = binBounds[bb + 5];
        }
        rightAcc += binCount[k];
        const lc = leftCountAcc[k - 1];
        if (lc === 0 || rightAcc === 0) continue;
        const cost = lc * leftArea[k - 1] + rightAcc * halfArea(ax1 - ax0, ay1 - ay0, az1 - az0);
        if (cost < bestCost) {
          bestCost = cost;
          bestAxis = axis;
          bestPos = cmin + (cmax - cmin) * (k / BIN_COUNT);
        }
      }
    }

    let leftCount: number;
    if (bestAxis < 0) {
      // Every centroid coincides on every axis; split by index to terminate.
      leftCount = count >> 1;
    } else {
      let i = first;
      let j = first + count - 1;
      while (i <= j) {
        if (centroids[order[i] * 3 + bestAxis] < bestPos) {
          i++;
        } else {
          const tmp = order[i];
          order[i] = order[j];
          order[j] = tmp;
          j--;
        }
      }
      leftCount = i - first;
      if (leftCount === 0 || leftCount === count) leftCount = medianSplit(first, count, bestAxis);
    }

    const left = nodesUsed++;
    const right = nodesUsed++;
    leftFirst[node] = left;
    primCount[node] = 0;
    subdivide(left, first, leftCount, depth + 1);
    subdivide(right, first + leftCount, count - leftCount, depth + 1);
  };

  subdivide(0, 0, n, 0);
  return { nodeCount: nodesUsed, maxDepth };
};
